import React, { useEffect, useState } from 'react';
import { useRoute } from '@react-navigation/native';

import { isPhone, JOB_STATUSES, Popup, withAbortableErrorHandler } from '@helpers';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { OperationSearchItem, ProductionOrderSearchItem } from '@interfaces/search';
import { useGetMachineByJob } from '@v2/pages/common/hooks/useGetMachineByJob';
import { scheduledProductionOperationIdParam } from '@components/operatorPanel/constants/operatorPanel.constants';
import { TRACKING_EVENTS } from '@services/tracking';
import FinishJobModal from '@v2/components/FinishJobModal';
import { getFinishJobModalPopupConfig } from '@v2/components/FinishJobModal/helpers/popupConfig';
import useIsFinishJobEnabled from '@v2/components/FinishJobModal/hooks/useIsFinishJobEnabled';
import JobMachinesModal from '@v2/pages/machines/modules/now/components/Job/components/JobMachinesModal';
import type { WithScaleFont } from '@interfaces/base';
import type { JobContentProps } from '@components/operatorPanel/components/OperatorPanelInformation/components/OperatorPanelInformationJobsScreen/JobContent';

export interface UseManageJobContentParams extends Pick<JobContentProps, 'job' | 'operationsSearch' | 'onJobFinished'>, WithScaleFont {}

export const useManageJobContent = ({ job, operationsSearch, onJobFinished, scale = initialScale, font = initialFont }: UseManageJobContentParams) => {
  const [similarJobsCount, setSimilarJobsCount] = useState(0);
  const [similarJobsQuantity, setSimilarJobsQuantity] = useState(0);
  const [repeatCount, setRepeatCount] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);

  const route = useRoute();
  const selectedJobId = route.params?.[scheduledProductionOperationIdParam];
  const isMachinePerspective = selectedJobId == null;

  const [selectedMachineForJob, setSelectedMachineForJob] = useState<{ name: string; id: number | null }>({ name: '', id: null });

  const machineByJobStatus = useGetMachineByJob(job);

  useEffect(() => {
    if (!job) {
      setSimilarJobsCount(0);
      setRepeatCount(0);

      return;
    }

    const abortController = new AbortController();
    const { signal } = abortController;

    // Two calls target disjoint sets for the same part:
    //  - active operations  → similar jobs (count + summed quantityTarget)
    //  - inactive operations → repeat count
    const searchByActive = (isActive: boolean) =>
      operationsSearch({
        data: {},
        filter: { oDataFilter: `PartId eq ${job.partId} and IsActive eq ${isActive}`, orderBy: ['ModifiedOn desc'] },
        select: ['QuantityTarget'],
        resetHistory: true,
        ignoreHistory: true,
        signal,
      });

    withAbortableErrorHandler({
      promise: Promise.all([searchByActive(true), searchByActive(false)]),
      signal,
      errorMessage: 'Error loading similar jobs',
      onSuccess: ([similarJobsData = {}, repeatCountData = {}]) => {
        setSimilarJobsCount(similarJobsData.totalResultCount);
        setRepeatCount(repeatCountData.totalResultCount);
        setSimilarJobsQuantity(
          similarJobsData.searchResult?.reduce((acc: number, job: Pick<ProductionOrderSearchItem, 'isActive' | 'quantityTarget'>) => {
            return acc + (job.quantityTarget ?? 0);
          }, 0)
        );
      },
    });

    return () => abortController.abort();
  }, [job?.partId]);

  const machineName = machineByJobStatus?.machine?.machineName;
  const isFinishJobEnabled = useIsFinishJobEnabled();

  const toggleMachines = () => {
    if (!job) return;
    Popup({
      Component: (props: any) => (
        <JobMachinesModal
          job={job}
          jobIds={[job.productionOperationId]}
          customOnSelect={(machineNameId) => setSelectedMachineForJob(machineNameId)}
          {...props}
        />
      ),
    });
  };

  const linkedMachineId = machineByJobStatus?.machine?.machineId ?? -1;
  // closeProductionOperation doesn't require machineId — don't gate on linkedMachineId or NotStarted jobs (empty machines array) stay un-finishable.
  const showMarkAsFinished = isFinishJobEnabled && job?.status !== JOB_STATUSES.FINISHED && !isPhone;

  const handleMarkAsFinished = () => {
    if (!job || !isFinishJobEnabled) return;
    Popup({
      Component: (p: { hide: () => void }) => (
        <FinishJobModal
          hide={p.hide}
          item={job}
          machineId={linkedMachineId}
          originScreen={TRACKING_EVENTS.OPERATOR_PANEL}
          onFinishSuccess={onJobFinished ? () => onJobFinished(job.productionOperationId) : undefined}
          scale={scale}
          font={font}
        />
      ),
      ...getFinishJobModalPopupConfig(scale),
    });
  };

  return {
    similarJobsCount,
    similarJobsQuantity,
    repeatCount,
    containerWidth,
    setContainerWidth,
    isMachinePerspective,
    selectedMachineForJob,
    machineByJobStatus,
    machineName,
    showMarkAsFinished,
    toggleMachines,
    handleMarkAsFinished,
  };
};
